import { useNavigate } from "react-router-dom";
import {
  ResetFormData,
  ResetFormTouched,
  ShowAlertProps,
} from "../../Components/common/types";
import { baseURL, resetPassword, validateOTP } from "../../config/variables";

const handleForgotAndResetSubmit = async <T extends Partial<ResetFormData>>(
  resetForgotFormData: T,
  setResetForgotFormData: React.Dispatch<React.SetStateAction<T>>,
  options: {
    //all options are required now for both forgot and reset forms
    setTouched: React.Dispatch<React.SetStateAction<Partial<ResetFormTouched>>>;
    validateWrapper: () => boolean;
    showAlert: ShowAlertProps["showAlert"];
    showLoader: ShowAlertProps["showLoader"];
    closeAlert: ()=> void,
  },
  isResetForm: boolean, //Determine if it's a enter email form or reset form
  navigate: ReturnType<typeof useNavigate> //navigate for redirection
) => {
  const { setTouched, validateWrapper, showAlert, closeAlert, showLoader } = options;

  if (!validateWrapper()) {
    //check if form is valid on submission
    console.log("Validation Error: ", resetForgotFormData);

    showAlert(
      true,
      "Validation Error",
      "Please fill in all required fields",
      false
    );

    return;
  }

  // Show loader while processing, if defined
  showLoader?.(true);

  // Add purpose field only if validating OTP
  const requestBody = isResetForm
    ? resetForgotFormData
    : { ...resetForgotFormData, purpose: "password_reset" };

  // console.log("Data: ", requestBody);

  try {
    const endpoint = isResetForm
      ? baseURL + resetPassword
      : baseURL + validateOTP;

    const response = await fetch(endpoint, {
      method: "POST",
      credentials: "include", // Include cookies
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 500) {
        showAlert(true, "Error", errorData.message, false);
      } else {
        showAlert(true, "Error", errorData.message, false);
      }

      return;
    }

    const data = await response.json();

    //Reset reset or email form data
    setResetForgotFormData(
      isResetForm
        ? ({ newPassword: "", confirmPassword: "" } as T)
        : ({ otp: "", email: "" } as T)
    );

    // Reset touched fields both forgot and reset forms
    setTouched(
      isResetForm
        ? { newPassword: false, confirmPassword: false }
        : { otp: false, email: false }
    );

    // Hide loader if defined and show success alert
    const successMessage = data.message;
    showLoader?.(false);
    showAlert(false, "Success", successMessage, false, undefined, undefined, false);
    setTimeout(() => {
      navigate(isResetForm ? "/dashboard/login" : "/dashboard/reset-password");
      closeAlert(); //close alert
    }, 2000); //Redirect after showing alert
  } catch (error) {
    console.error("Error from front end:", error); //ensure you create errors that can only be seen in dev mode
    showAlert(
      true,
      "Error",
      "An unexpected error occurred. Please try again later.", //rear cases
      false
    );
  } finally {
    // Hide loader regardlessly
    showLoader?.(false);
  }
};

export default handleForgotAndResetSubmit;
